import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/authContext";
import { useTimerContext } from "@/lib/timerContext";
import { api, fetchWithAuth, Account, ActivityCode, TimeEntry, PayPeriod } from "@/lib/api";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { StatusPill } from "@/components/custom/StatusPill";
import { format, addDays, subDays, parseISO, isSameDay, isAfter } from "date-fns";
import { Edit2, Trash2, Clock, Loader2, ChevronLeft, ChevronRight, Plus, CalendarIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTimeFormat, formatHours } from "@/lib/userPrefs";
import { Check } from "lucide-react";

export default function Today() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { openSheetForEdit, openSheet, lastSavedAt } = useTimerContext();
  const [timeFmt] = useTimeFormat();

  const todayDate = new Date();
  const todayStr = format(todayDate, "yyyy-MM-dd");

  const [selectedDate, setSelectedDate] = useState<Date>(todayDate);
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const isViewingToday = isSameDay(selectedDate, todayDate);
  const isFutureDate = isAfter(selectedDate, todayDate);
  const maxFutureDate = addDays(todayDate, 14);

  const [currentPayPeriod, setCurrentPayPeriod] = useState<PayPeriod | null>(null);
  const [previousPayPeriod, setPreviousPayPeriod] = useState<PayPeriod | null>(null);
  const [nextPayPeriod, setNextPayPeriod] = useState<PayPeriod | null>(null);
  const [todaysPayPeriod, setTodaysPayPeriod] = useState<PayPeriod | null>(null);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [accountsData, codesData, entriesData, navData, todayNavData] = await Promise.all([
        api.getAccounts(),
        api.getActivityCodes(),
        api.getTimeEntries({ startDate: selectedDateStr, endDate: selectedDateStr }),
        fetchWithAuth<{ current: PayPeriod | null; previous: PayPeriod | null; next: PayPeriod | null }>(
          `/api/pay-periods/navigation/${selectedDateStr}`,
        ),
        fetchWithAuth<{ current: PayPeriod | null; previous: PayPeriod | null; next: PayPeriod | null }>(
          `/api/pay-periods/navigation/${todayStr}`,
        ),
      ]);

      setAccounts(accountsData);
      setActivityCodes(codesData);
      setEntries(entriesData);
      setCurrentPayPeriod(navData.current);
      setPreviousPayPeriod(navData.previous);
      setNextPayPeriod(navData.next);
      setTodaysPayPeriod(todayNavData.current);
    } catch (error: any) {
      toast({ title: "Error loading data", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [selectedDateStr, todayStr, toast]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  // Reload when the sheet saves an entry
  useEffect(() => {
    if (lastSavedAt) loadData();
  }, [lastSavedAt]);

  // When navigated here from Timesheet with ?edit=<id>, open the edit sheet for that entry
  const [location, setLocation] = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit");
    if (!editId || !entries.length || !accounts.length) return;
    const entry = entries.find((e) => e.id === editId);
    if (entry) {
      openSheetForEdit(entry, accounts);
      // Clean the URL so a page refresh doesn't re-open the sheet
      setLocation("/today", { replace: true });
    }
  }, [entries, accounts]);

  const handleDelete = async (id: string) => {
    try {
      await api.deleteTimeEntry(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast({ title: "Entry deleted" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handlePrevDay = () => { setSelectedDate(subDays(selectedDate, 1)); setLoading(true); };
  const handleNextDay = () => { if (!isAfter(selectedDate, maxFutureDate)) { setSelectedDate(addDays(selectedDate, 1)); setLoading(true); } };
  const handleGoToToday = () => { setSelectedDate(todayDate); setLoading(true); };

  const handlePrevPeriod = () => {
    if (previousPayPeriod) {
      const end = parseISO(previousPayPeriod.end_date);
      setSelectedDate(isAfter(end, todayDate) ? todayDate : end);
      setLoading(true);
    }
  };

  const handleNextPeriod = () => {
    if (nextPayPeriod) {
      const start = parseISO(nextPayPeriod.start_date);
      setSelectedDate(isAfter(start, todayDate) ? todayDate : start);
      setLoading(true);
    }
  };

  const formatPeriodRange = (period: PayPeriod | null) => {
    if (!period) return "No period";
    return `${format(parseISO(period.start_date), "MMM d")} – ${format(parseISO(period.end_date), "MMM d")}`;
  };

  const selectedDayEntries = entries.filter((e) => e.entry_date === selectedDateStr);
  const canEditDate = currentPayPeriod?.status !== "closed";
  const totalHours = selectedDayEntries.reduce((sum, e) => sum + e.minutes / 60, 0);
  const progress = (totalHours / 8) * 100;
  const isViewingDifferentPeriod = currentPayPeriod && todaysPayPeriod && currentPayPeriod.id !== todaysPayPeriod.id;

  const payPeriodHeader = (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={handlePrevPeriod} disabled={!previousPayPeriod} className="h-8 px-2" data-testid="button-prev-period">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="text-center px-3">
        <span className="text-sm font-medium" data-testid="text-period-range">{formatPeriodRange(currentPayPeriod)}</span>
        {currentPayPeriod?.status === "closed" && <span className="ml-2 text-xs text-amber-600 font-medium">(Closed)</span>}
        {isViewingDifferentPeriod && (
          <Button variant="link" className="h-auto p-0 ml-2 text-xs" onClick={handleGoToToday}>Back to Today</Button>
        )}
      </div>
      <Button
        variant="ghost" size="sm" onClick={handleNextPeriod}
        disabled={!nextPayPeriod || (nextPayPeriod ? isAfter(parseISO(nextPayPeriod.start_date), todayDate) : true)}
        className="h-8 px-2" data-testid="button-next-period"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );

  if (loading) {
    return (
      <Layout headerContent={payPeriodHeader}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout headerContent={payPeriodHeader}>
      <div className="flex flex-col gap-4 max-w-2xl mx-auto">
        {/* Day Navigation */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={handlePrevDay} className="h-8 w-8" data-testid="button-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 group text-left" data-testid="text-selected-date">
                  <h1 className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                    {isViewingToday ? "Today" : format(selectedDate, "EEEE")}{" "}
                    <span className="text-muted-foreground font-normal text-sm">— {format(selectedDate, "MMM d, yyyy")}</span>
                  </h1>
                  <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-primary transition-colors mt-0.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => { if (date) { setSelectedDate(date); setLoading(true); } }}
                  startMonth={subDays(todayDate, 14)}
                  endMonth={maxFutureDate}
                  disabled={(date: Date) => date > maxFutureDate || date < subDays(todayDate, 14)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {!isViewingToday && (
              <Button variant="link" className="h-auto p-0 text-xs" onClick={handleGoToToday}>Back to Today</Button>
            )}
          </div>
          <Button variant="outline" size="icon" onClick={handleNextDay} disabled={isAfter(selectedDate, maxFutureDate)} className="h-8 w-8" data-testid="button-next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Daily Progress */}
        <div className="bg-muted/30 rounded-lg p-4 border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Daily Progress</span>
            <div className="flex items-baseline gap-1">
              <span className="text-xl font-bold text-primary" data-testid="text-total-hours">{formatHours(totalHours, timeFmt, 1)}</span>
              <span className="text-sm text-muted-foreground">/ 8h</span>
            </div>
          </div>
          <Progress value={Math.min(progress, 100)} className="h-2" />
          {progress >= 100 && (
            <div className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
              <Check className="w-3 h-3" /> Goal Met
            </div>
          )}
        </div>

        {/* Period closed warning */}
        {!canEditDate && (
          <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            This pay period is closed.
          </div>
        )}
        {isFutureDate && canEditDate && (
          <div className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            Viewing a future date — entries will be saved for {format(selectedDate, "MMMM d, yyyy")}.
          </div>
        )}

        {/* Logged Entries */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="py-3 border-b border-border/50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">Logged Entries</CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono bg-muted px-2 py-1 rounded text-muted-foreground" data-testid="text-entry-count">
                  {selectedDayEntries.length} entries
                </span>
                {canEditDate && (
                  <Button size="sm" variant="outline" className="h-7 px-2 gap-1 text-xs" onClick={() => openSheet(selectedDateStr)} data-testid="button-add-entry">
                    <Plus className="w-3 h-3" /> Add Entry
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {selectedDayEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-muted-foreground p-10 text-center">
                <Clock className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm">No time logged for this date.</p>
                {canEditDate && (
                  <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => openSheet(selectedDateStr)}>
                    <Plus className="w-3 h-3" /> Add Entry
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {selectedDayEntries.map((entry: TimeEntry) => {
                  const account = accounts.find((a) => a.id === entry.account_id);
                  const code = activityCodes.find((c) => c.code === entry.activity_code);
                  const isHoliday = code?.category === "holiday";
                  const isPTO = code?.category === "pto";
                  const hours = entry.minutes / 60;

                  const formatStoredTime = (t: string) =>
                    new Date(t).toLocaleTimeString("en-US", {
                      hour: "numeric", minute: "2-digit", hour12: true,
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    });

                  const startTimeStr = entry.start_time ? formatStoredTime(entry.start_time) : null;
                  const endTimeStr = entry.end_time ? formatStoredTime(entry.end_time) : null;

                  return (
                    <div key={entry.id} className="p-3 hover:bg-muted/30 transition-colors group flex items-start gap-3" data-testid={`entry-${entry.id}`}>
                      <div className="min-w-[3.5rem] text-center">
                        <div className="text-base font-bold text-foreground">{formatHours(hours, timeFmt)}</div>
                        <div className="flex items-center justify-center gap-1 mt-0.5">
                          {entry.billable && <span className="text-[9px] font-semibold text-emerald-600 bg-emerald-100 px-1 py-0.5 rounded">$</span>}
                          {isHoliday && <span className="text-[9px] font-semibold text-blue-600 bg-blue-100 px-1 py-0.5 rounded">Holiday</span>}
                          {isPTO && <span className="text-[9px] font-semibold text-amber-600 bg-amber-100 px-1 py-0.5 rounded">PTO</span>}
                        </div>
                      </div>

                      <div className="flex-1 space-y-0.5 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-foreground text-sm truncate">{account?.account_name || "No Account"}</h4>
                          <StatusPill status={entry.status} className="scale-90 origin-right" />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-mono text-xs bg-muted px-1 rounded">{code?.code}</span>
                          <span className="truncate">{code?.name}</span>
                        </div>
                        {(startTimeStr || endTimeStr) && (
                          <div className="text-xs text-muted-foreground">
                            {startTimeStr && endTimeStr ? `${startTimeStr} – ${endTimeStr}` : startTimeStr || endTimeStr}
                          </div>
                        )}
                        {entry.notes && <p className="text-xs text-muted-foreground/80 italic truncate">"{entry.notes}"</p>}
                      </div>

                      {entry.status === "draft" && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSheetForEdit(entry, accounts)} data-testid={`button-edit-${entry.id}`}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(entry.id)} data-testid={`button-delete-${entry.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
